import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../../lib/brand";

const LINES = ["The Water", "That Carries", "Intention"];
const BASE_DELAY = 2.3;

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      delay: BASE_DELAY + i * 0.16,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#030303]"
    >
      {/* teal radial glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] teal-radial" />

      {/* floating bottle image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full lg:w-[52%] z-10"
      >
        <motion.div
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-full w-full"
        >
          <motion.img
            src={IMAGES.heroNature}
            alt="Manifest Waterboat bottle amid nature and a sacred spring"
            className="h-full w-full object-cover"
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303] via-[#030303]/30 to-transparent lg:via-[#030303]/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent" />
          <div className="absolute inset-0 bg-black/40 lg:hidden" />
        </motion.div>
      </motion.div>

      {/* headline */}
      <motion.div
        style={{ y: textY }}
        className="relative z-20 mx-auto max-w-[1400px] px-6 lg:px-12 min-h-screen flex flex-col justify-center"
      >
        <h1 className="font-serif font-light leading-[0.92] tracking-tighter text-[clamp(2.4rem,8.25vw,7.5rem)]">
          {LINES.map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "italic text-teal-300 text-glow" : "text-zinc-100"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: BASE_DELAY + 0.9, duration: 1 }}
          className="mt-8 max-w-md text-zinc-400 text-base lg:text-lg font-light"
        >
          Bottled from the sacred aquifers of Nashik. Charged with positive
          intention. Water that remembers — and returns it to you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: BASE_DELAY + 1.1, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href="#intention"
            data-testid="hero-cta-intention"
            className="group inline-flex items-center gap-3 rounded-full bg-teal-400 px-7 py-3.5 text-sm font-medium text-black transition-all duration-300 hover:bg-teal-300"
          >
            Set Your Intention
            <ArrowDown size={16} className="transition-transform group-hover:translate-y-0.5" />
          </a>
        </motion.div>
      </motion.div>

      {/* scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: BASE_DELAY + 1.5, duration: 1 }}
        className="absolute bottom-8 left-6 lg:left-12 z-20 flex items-center gap-3"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">SCROLL</span>
        <motion.span
          className="h-8 w-px bg-gradient-to-b from-teal-400 to-transparent"
          animate={{ scaleY: [0.3, 1, 0.3], transformOrigin: "top" }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
